import { type ContentItem, type Locale, withLongItemsLast } from '@kidshub/contracts';

/**
 * Rearranging a collection by hand, and the one-tap sorts that start it off.
 *
 * A quick sort is a starting point and not a mode: it orders the list once, and
 * from then on the order is simply the order, to be dragged about. A mode that
 * kept re-sorting would undo the first episode a parent moved by hand.
 */

export type QuickSort = 'NAME' | 'DURATION' | 'ADDED';

/** Moves one entry to a new index, clamped to the list. */
export const moveTo = <Id>(order: readonly Id[], id: Id, to: number): Id[] => {
  const from = order.indexOf(id);
  if (from === -1) {
    return [...order];
  }
  const next = order.filter((entry) => entry !== id);
  next.splice(Math.max(0, Math.min(next.length, to)), 0, id);
  return next;
};

export const quickSort = (
  items: readonly ContentItem[],
  sort: QuickSort,
  locale: Locale,
  longItemsLast: boolean,
): ContentItem['id'][] => {
  const compare: Record<QuickSort, (a: ContentItem, b: ContentItem) => number> = {
    NAME: (a, b) => a.title.localeCompare(b.title, locale, { numeric: true }),
    DURATION: (a, b) => (a.durationSeconds ?? 0) - (b.durationSeconds ?? 0),
    ADDED: (a, b) => a.createdAt.localeCompare(b.createdAt),
  };

  const sorted = [...items].sort(compare[sort]).map((item) => item.id);
  if (!longItemsLast) {
    return sorted;
  }

  const durations = new Map(items.map((item) => [item.id, item.durationSeconds]));
  return withLongItemsLast(sorted, (id) => durations.get(id) ?? null);
};
